'use client'

import { useEffect } from 'react'
import { usePathname } from 'next/navigation'

/**
 * Komponente zur globalen Bereitstellung der Header-Höhe als CSS-Variable (--header-height).
 * Reagiert robust auf SPA-Navigation, Streaming (Suspense) und unterschiedliche Header-Layouts.
 */
export function HeaderHeightVar() {
    const pathname = usePathname();

    useEffect(() => {
        if (typeof window === 'undefined') return;

        let rafId: number | null = null;
        let ro: ResizeObserver | null = null;
        let mo: MutationObserver | null = null;
        let observedEl: HTMLElement | null = null;
        let retryCount = 0;
        const maxRetries = 20;

        const updateHeight = (height: number) => {
            // Nimm gemessene Höhe oder Fallback auf 64px, wenn nichts gefunden wurde
            const finalHeight = height > 0 ? height : 64;
            document.documentElement.style.setProperty('--header-height', `${finalHeight}px`);
        };

        const findVisibleHeader = (): HTMLElement | null => {
            const headers = Array.from(document.querySelectorAll('header'));
            // Finde den Header, der aktuell im Viewport sichtbar ist (Höhe > 0)
            const visible = headers.find(el => (el as HTMLElement).offsetHeight > 0);
            return (visible as HTMLElement) || (headers[0] as HTMLElement) || null;
        };

        const measure = () => {
            const el = findVisibleHeader();
            if (el) {
                const rect = el.getBoundingClientRect();
                updateHeight(rect.height);
            } else {
                updateHeight(64);
            }
        };

        const setupResizeObserver = (el: HTMLElement) => {
            // Falls wir bereits dieses Element beobachten, nichts tun
            if (observedEl === el) return;
            
            if (ro) {
                ro.disconnect();
            }
            
            observedEl = el;
            if ('ResizeObserver' in window) {
                ro = new ResizeObserver(() => {
                    measure();
                });
                ro.observe(el);
            }
            measure();
        };

        const tryInit = (): boolean => {
            const el = findVisibleHeader();
            // Erfolgreich, wenn Header gefunden wurde UND er eine Höhe > 0 hat
            if (el && el.offsetHeight > 0) {
                setupResizeObserver(el);
                return true;
            }
            return false;
        };

        const startInitialization = () => {
            retryCount = 0;
            const attempt = () => {
                if (tryInit()) return;
                
                if (retryCount < maxRetries) {
                    retryCount++;
                    rafId = window.requestAnimationFrame(attempt);
                } else {
                    // Finaler Fallback nach allen Retries
                    const el = findVisibleHeader();
                    if (el) setupResizeObserver(el);
                    else measure();
                }
            };
            rafId = window.requestAnimationFrame(attempt);
        };

        startInitialization();

        // MutationObserver beobachtet das Hinzufügen/Entfernen von Elementen im DOM (für Layout-Wechsel)
        if ('MutationObserver' in window) {
            mo = new MutationObserver(() => {
                const el = findVisibleHeader();
                if (el && el !== observedEl && el.offsetHeight > 0) {
                    setupResizeObserver(el);
                } else if (!el && observedEl) {
                    observedEl = null;
                    if (ro) ro.disconnect();
                    measure();
                }
            });
            mo.observe(document.body, { childList: true, subtree: true });
        }

        const resizeHandler = () => measure();
        window.addEventListener('resize', resizeHandler);

        return () => {
            if (rafId) cancelAnimationFrame(rafId);
            if (ro) ro.disconnect();
            if (mo) mo.disconnect();
            window.removeEventListener('resize', resizeHandler);
        };
    }, [pathname]); // Re-Initialisierung bei jeder Client-Side Navigation

    return null;
}